import type { DateRange, HubEvent } from './types';
import { getScheduleSortStart, parseDateTime, resolveSchedule } from './schedule';

export { parseDateTime } from './schedule';

/** 개발 환경에서는 시작일 전에도 노출 (QA용) */
const isDev = process.env.ENVIRONMENT === 'dev';

// ── 운영 상태
export type EventMainStatus = 'live' | 'soon' | 'always' | 'recruiting';

// ── 서브 상태 (live + 모집 중 겹칠 때만 — always/recruiting 단독과는 조합 안 함)
export type EventSubStatus = 'recruiting';

export interface EventStatus {
  main: EventMainStatus;
  sub?: EventSubStatus;
  /** 카드/슬라이드에 표시할 기간 — 상태에 따라 period 또는 recruitment 중 하나 */
  range: DateRange;
}

function getRecruitmentActive(recruitment: DateRange, now: Date): { active: boolean; end?: Date } {
  if (!recruitment.start) return { active: false };
  const recStart = parseDateTime(recruitment.start);
  const recEnd = recruitment.end ? parseDateTime(recruitment.end, true) : undefined;
  const active = now >= recStart && (!recEnd || now <= recEnd);
  return { active, end: recEnd };
}

function isSoonBeforeEnd(end: Date, soonDays: number, now: Date): boolean {
  const soonThreshold = new Date(end);
  soonThreshold.setDate(soonThreshold.getDate() - soonDays);
  return now >= soonThreshold;
}

function getRecruitmentOnlyStatus(
  recruitment: DateRange,
  soonDays: number,
  now: Date,
): EventStatus | null {
  if (!recruitment.start) return null;

  const recStart = parseDateTime(recruitment.start);

  // 운영 환경: 시작일 전이면 노출하지 않음
  if (!isDev && now < recStart) return null;

  if (recruitment.end) {
    const recEnd = parseDateTime(recruitment.end, true);
    if (now > recEnd) return null;

    if (isSoonBeforeEnd(recEnd, soonDays, now)) {
      return { main: 'soon', range: recruitment };
    }
  }

  return { main: 'recruiting', range: recruitment };
}

export function getEventStatus(event: HubEvent, now = new Date()): EventStatus | null {
  const soonDays = event.soonDays ?? 3;
  const { period, recruitment } = resolveSchedule(event, now);

  // ── period 없음: recruitment로만 노출/종료 결정
  if (!period) {
    if (!recruitment) return null;
    return getRecruitmentOnlyStatus(recruitment, soonDays, now);
  }

  // ── period 있음: 종료일만 노출 차단 (시작일 무시)
  if (period.end) {
    const end = parseDateTime(period.end, true);
    if (now > end) return null;
  }

  const start = parseDateTime(period.start);
  const periodStarted = now >= start;

  // ── period 시작 전
  if (!periodStarted) {
    // recruitment 활성 중이면 recruiting 상태로 노출
    if (recruitment) {
      const recruitmentInfo = getRecruitmentActive(recruitment, now);
      if (recruitmentInfo.active) {
        if (recruitmentInfo.end && isSoonBeforeEnd(recruitmentInfo.end, soonDays, now)) {
          return { main: 'soon', range: recruitment };
        }
        return { main: 'recruiting', range: recruitment };
      }
    }

    // 운영 환경: recruitment 없거나 비활성 → 시작 전이므로 노출하지 않음
    if (!isDev) return null;

    // 개발 환경: QA를 위해 시작 전에도 노출
    if (!period.end) {
      return { main: 'always', range: { start: period.start } };
    }

    const end = parseDateTime(period.end, true);
    if (isSoonBeforeEnd(end, soonDays, now)) {
      return { main: 'soon', range: period };
    }

    return { main: 'live', range: period };
  }

  // end 없으면 상시 — 모집 중과 절대 조합하지 않음
  if (!period.end) {
    return { main: 'always', range: { start: period.start } };
  }

  const end = parseDateTime(period.end, true);
  if (isSoonBeforeEnd(end, soonDays, now)) {
    return { main: 'soon', range: period };
  }

  if (recruitment) {
    const recruitmentInfo = getRecruitmentActive(recruitment, now);
    if (recruitmentInfo.active) {
      return { main: 'live', sub: 'recruiting', range: period };
    }
  }

  return { main: 'live', range: period };
}

/** 운영 중인 이벤트만 반환 */
export function getActiveEvents(events: HubEvent[]): HubEvent[] {
  return events.filter((event) => getEventStatus(event) !== null);
}

export function getEventKey(event: HubEvent): string {
  return [
    event.order ?? 'auto',
    event.id,
    getScheduleSortStart(resolveSchedule(event)),
    event.category,
  ].join('-');
}
